import { ShieldCheck, Link2, FileCheck, Hash } from "lucide-react";
import { cn } from "@/lib/utils";

interface IntegrityItem {
  label: string;
  value: string;
  verified: boolean;
}

interface AuditIntegrityPanelProps {
  title?: string;
  items: IntegrityItem[];
  hashPreview?: string;
  className?: string;
}

export function AuditIntegrityPanel({
  title = "Integrity & Audit",
  items,
  hashPreview,
  className,
}: AuditIntegrityPanelProps) {
  const allVerified = items.every((item) => item.verified);

  return (
    <div
      className={cn(
        "rounded-lg border border-navy-deep/10 bg-navy-deep/3 p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck
            className={cn(
              "h-5 w-5",
              allVerified ? "text-green-approved" : "text-amber-warning",
            )}
          />
          <h4 className="font-semibold text-navy-deep">{title}</h4>
        </div>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-semibold",
            allVerified
              ? "bg-green-50 text-green-700"
              : "bg-amber-50 text-amber-800",
          )}
        >
          {allVerified ? "Verified" : "Review needed"}
        </span>
      </div>

      <ul className="mt-4 space-y-2">
        {items.map((item) => (
          <li
            key={item.label}
            className="flex items-center justify-between gap-4 rounded-md bg-card-white px-3 py-2 text-sm"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              {item.label === "On-chain anchor" ? (
                <Link2 className="h-3.5 w-3.5" />
              ) : item.label === "Export hash" ? (
                <Hash className="h-3.5 w-3.5" />
              ) : (
                <FileCheck className="h-3.5 w-3.5" />
              )}
              {item.label}
            </span>
            <span
              className={cn(
                "font-mono text-xs",
                item.verified ? "text-green-approved" : "text-amber-warning",
              )}
            >
              {item.value}
            </span>
          </li>
        ))}
      </ul>

      {hashPreview ? (
        <div className="mt-3 rounded border border-dashed border-navy-blueprint/20 bg-navy-deep/5 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Batch proof
          </p>
          <p className="mt-0.5 truncate font-mono text-xs text-navy-deep">{hashPreview}</p>
        </div>
      ) : null}
    </div>
  );
}
